<template>
  <button
    class="wrapper-button"
    :class="{ selected: isSelectedMic }"
    :disabled="isDeviceLoading"
    @click="emit('changeDevice', microphone)"
  >
    <div class="wrapper-mic-model">
      <p class="small mic-model">
        {{ microphone.label || 'Default' }}
      </p>
    </div>

    <div class="wrapper-icon">
      <SpinnerIcon v-if="showSpinner" />
      <CheckIcon v-else-if="isSelectedMic" class="icon-smaller icon-white icon-check" />
      <template v-if="usingTss">
        <template v-if="microphone.voiceId">
          <Tooltip :tooltipText="t('tooltip_device_registered_voice')" class="icon-registred-wrap">
            <IconVoice class="icon-small icon-dim" />
          </Tooltip>

          <Tooltip :tooltipText="t('tooltip_device_registered_voice_delete')" class="icon-registred-wrap">
            <button
              class="button-icon button-icon-small button-glass"
              @click.stop.prevent="emit('deleteVoice', microphone.voiceId)"
            >
              <IconCircleMinus class="icon-dim icon-smaller" />
            </button>
          </Tooltip>
        </template>

        <Tooltip v-else :tooltipText="t('tooltip_device_registered_voice_none')" class="icon-registred-wrap">
          <button
            class="button-icon button-icon-small button-glass"
            @click.stop.prevent="emit('openVoice', microphone)"
          >
            <IconCirclePlus class="icon-dim icon-smaller" />
          </button>
        </Tooltip>
      </template>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CheckIcon from '@/components/icons/IconCheck.vue';
import IconCircleMinus from '@/components/icons/IconCircleMinus.vue';
import IconCirclePlus from '@/components/icons/IconCirclePlus.vue';
import SpinnerIcon from '@/components/icons/IconSpinner.vue';
import IconVoice from '@/components/icons/IconVoice.vue';
import Tooltip from '@/components/shared/composite/Tooltip.vue';
import { useMicrophoneStore } from '@/stores/microphone';
import { IMicrophoneWithVoice } from '@/types/components';

const microphoneStore = useMicrophoneStore();

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    microphone: Partial<IMicrophoneWithVoice>;
    isDeviceLoading: boolean;
    usingTss: boolean;
  }>(),
  {
    microphone: () => ({}) as Partial<IMicrophoneWithVoice>,
    isDeviceLoading: false,
    usingTss: false,
  }
);

const emit = defineEmits<{
  changeDevice: [device: Partial<IMicrophoneWithVoice>];
  openVoice: [device: Partial<IMicrophoneWithVoice>];
  deleteVoice: [deviceId: number];
}>();

const isSelectedMic = computed<boolean>(() => {
  return microphoneStore.selectedMicrophone.id === props.microphone.id;
});

// 짧은 로딩일때는 로딩 컴포넌트 렌더링 x (추가)
const showSpinner = ref(false);
let spinnerTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.isDeviceLoading,
  (newVal) => {
    if (newVal) {
      spinnerTimer = setTimeout(() => {
        showSpinner.value = true;
      }, 300);
    } else {
      if (spinnerTimer) clearTimeout(spinnerTimer);
      showSpinner.value = false;
    }
  }
);

onUnmounted(() => {
  if (spinnerTimer) clearTimeout(spinnerTimer);
});
</script>

<style scoped lang="scss">
.wrapper-button {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem;
  align-items: center;
  gap: 0.5rem;
  color: var(--font-dim);
  border-radius: 8px;
  z-index: 0;
  overflow: visible;
  transition:
    border-radius 0.5s ease,
    background-color 0.5s ease;
}

.wrapper-button:hover {
  background-color: var(--surface-hover-gray-dim);
}

.selected {
  background-color: var(--surface-gray-dim);
  color: var(--font-default);
}

.wrapper-mic-model {
  flex: 1;
  min-width: 0;
}

.mic-model {
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
}

.wrapper-icon {
  display: flex;
  gap: 0.5rem;
  width: fit-content;
  align-items: center;
  justify-content: flex-end;
}

.icon-registred-wrap {
  display: flex;

  align-items: center;

  flex-shrink: 0;

  gap: 0.5rem;
}

.icon-check {
  background-color: var(--success);
  border-radius: 50%;
  flex-shrink: 0;
  padding: 2px;
}
</style>
